import { Controller } from "@hotwired/stimulus"

// Per-cell click-to-edit. The span renders the current value; on click
// it's replaced with an input. On blur or Enter, PATCH the change and
// swap back to a span (optimistic — show new value immediately, rollback
// on error).
export default class extends Controller {
  static values = {
    taskId: String,
    field: String,
    type: { type: String, default: "text" },
    url: String,
    displayFormat: { type: String, default: "" }
  }

  connect() {
    this.fieldUpdatedHandler = this.onFieldUpdated.bind(this)
    document.addEventListener("task:field-updated", this.fieldUpdatedHandler)
  }

  disconnect() {
    document.removeEventListener("task:field-updated", this.fieldUpdatedHandler)
  }

  // Listen for panel autosaves on the same task+field and refresh the
  // displayed text so the row reflects the panel edit without a reload.
  onFieldUpdated(event) {
    const { taskId, field, value } = event.detail
    if (!taskId || String(this.taskIdValue) !== String(taskId)) return
    if (this.fieldValue !== field) return
    if (this.editing) return  // user is mid-edit; don't yank text out from under them
    this.element.textContent = this.formatForDisplay(value)
  }

  edit(event) {
    // Stop the click from bubbling to the row's openSidePanel handler.
    // Clicking the editable text means "edit this cell", not "open the panel".
    event.stopPropagation()
    if (this.editing) return
    this.editing = true
    this.originalText = this.element.textContent
    this.originalValue = this.rawValueFromText(this.originalText)

    const input = document.createElement("input")
    input.type = this.typeValue
    input.value = this.originalValue
    input.className = "px-1 py-0.5 border border-[#C4A484] rounded text-sm focus:outline-none"
    input.style.minWidth = `${this.element.offsetWidth + 20}px`

    input.addEventListener("blur", () => this.save(input))
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); input.blur() }
      if (e.key === "Escape") { e.preventDefault(); this.cancel(input) }
    })

    this.element.replaceWith(input)
    this.inputEl = input
    input.focus()
    input.select?.()
  }

  rawValueFromText(text) {
    if (this.typeValue === "date" && text && text !== "—") {
      // Cell shows "5/24"; date input wants "YYYY-MM-DD". Convert.
      // Year is the current year for short-form display.
      const [m, d] = text.trim().split("/")
      if (m && d) {
        const y = new Date().getFullYear()
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
      }
      return ""
    }
    return text === "—" ? "" : text
  }

  async save(input) {
    if (input.dataset.cancelled === "true") return
    const newValue = input.value
    if (newValue === this.originalValue) {
      this.swapBackTo(this.originalText)
      return
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ field: this.fieldValue, value: newValue })
      })
      if (!response.ok) throw new Error(`Save failed: ${response.status}`)
      const data = await response.json()
      this.swapBackTo(this.formatForDisplay(data.value))
    } catch (err) {
      console.error("inline_cell save failed:", err)
      this.swapBackTo(this.originalText)
    }
  }

  cancel(input) {
    input.dataset.cancelled = "true"
    this.swapBackTo(this.originalText)
  }

  swapBackTo(text) {
    const span = document.createElement("span")
    span.textContent = text
    span.className = this.element?.className || "text-sm text-[#2D2D2D] hover:bg-[#F5F3F0] rounded px-1 py-0.5 inline-block"
    span.dataset.controller = "inline-cell"
    span.dataset.inlineCellTaskIdValue = this.taskIdValue
    span.dataset.inlineCellFieldValue = this.fieldValue
    span.dataset.inlineCellTypeValue = this.typeValue
    span.dataset.inlineCellUrlValue = this.urlValue
    if (this.displayFormatValue) span.dataset.inlineCellDisplayFormatValue = this.displayFormatValue
    span.dataset.action = "click->inline-cell#edit"
    this.inputEl.replaceWith(span)
    this.editing = false
    this.inputEl = null
  }

  formatForDisplay(value) {
    if (!value) return "—"
    if (this.displayFormatValue === "md" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const [, m, d] = value.match(/^\d{4}-(\d{2})-(\d{2})/)
      return `${parseInt(m, 10)}/${parseInt(d, 10)}`
    }
    return value
  }

  headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "Accept": "application/json"
    }
  }
}
